import React, { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import Avatar from "../ui/Avatar";
import { RichContent, isHtml } from "./richText";
import ConfirmModal from "../modals/ConfirmModal";
import CommentComposer from "./CommentComposer";
import { AttachmentTile } from "./attachments";
import { PRIORITIES, STATUS_LABELS } from "./constants";
import { formatDate } from "./shared";

const FIELD_LABELS = {
  title: "title",
  description: "description",
  taskType: "type",
  projects: "projects",
  squad: "squad",
  sprint: "sprint",
  assignees: "assignees",
  status: "status",
  priority: "priority",
  dueDate: "due date",
  qaRejectedCount: "QA rejection",
};

/** Display text for a stored old/new value. */
const valueText = (field, value) => {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) return value.length ? value.map((v) => v.label).join(", ") : null;
  if (typeof value === "object") return value.label;
  if (field === "status") return STATUS_LABELS[value] || value;
  if (field === "priority")
    return PRIORITIES.find((p) => p.value === value)?.label || value;
  if (field === "dueDate") return formatDate(value);
  return String(value);
};

const Value = ({ children }) => (
  <span className="inline-block max-w-full truncate align-bottom px-1.5 py-0.5 rounded-md bg-raise text-ink text-xs font-medium">
    {children}
  </span>
);

/** Human-readable sentence (after the actor's name) for a history entry. */
const describe = (item) => {
  const { type, field, from, to, meta = {} } = item;
  switch (type) {
    case "task_created":
      return <>created this task</>;
    case "comment_edited":
      return <>edited a comment</>;
    case "comment_deleted":
      return <>deleted a comment</>;
    case "attachment_added":
      return (
        <>
          {meta.onComment ? "attached " : "uploaded "}
          <Value>{item.attachment?.originalName || meta.name}</Value>
          {meta.onComment ? " to a comment" : ""}
        </>
      );
    case "attachment_removed":
      return (
        <>
          removed <Value>{item.attachment?.originalName || meta.name}</Value>
        </>
      );
    case "field_changed": {
      const label = FIELD_LABELS[field] || field;
      if (field === "description")
        return <>{meta.cleared ? "cleared" : "updated"} the description</>;
      if (field === "qaRejectedCount")
        return <>removed the QA rejection{from > 1 ? <> (×{from})</> : null}</>;
      const a = valueText(field, from);
      const b = valueText(field, to);
      if (!a && b)
        return (
          <>
            set the {label} to <Value>{b}</Value>
          </>
        );
      if (a && !b)
        return (
          <>
            removed the {label} <Value>{a}</Value>
          </>
        );
      return (
        <>
          changed the {label} from <Value>{a}</Value> to <Value>{b}</Value>
        </>
      );
    }
    default:
      return <>updated the task</>;
  }
};

const time = (value) =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
const fullTime = (value) => new Date(value).toLocaleString();

/** Comment text with the structured @mentions highlighted. */
const CommentBody = ({ comment, people }) => {
  // Rich (editor) comments carry their own mention chips.
  if (isHtml(comment.body)) return <RichContent value={comment.body} className="text-[13.5px]" />;
  const names = (comment.mentions || [])
    .map((id) => people.get(id)?.fullNameEn)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (!names.length) return <>{comment.body}</>;
  const escaped = names.map((n) =>
    `@${n}`.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );
  const parts = comment.body.split(new RegExp(`(${escaped.join("|")})`, "g"));
  return parts.map((part, i) =>
    names.some((n) => part === `@${n}`) ? (
      <span key={i} className="font-medium text-primary-accent">
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
};

/**
 * One feed entry: comments are cards (distinct from system history lines);
 * everything else is a one-line, human-readable history entry.
 */
const ActivityItem = ({
  item,
  access,
  people,
  mentionable,
  canRemoveFile,
  onEditComment,
  onDeleteComment,
  onRemoveFile,
  onPreview,
  onError,
}) => {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const actorName = item.actor?.fullNameEn || item.actor?.nickname || "Someone";

  if (item.type === "comment_added" && item.comment) {
    const c = item.comment;
    const deleted = Boolean(c.deletedAt);
    const mine = c.author?._id === access.userId;
    return (
      <li className="flex gap-3">
        <Avatar src={c.author?.photo} name={c.author?.fullNameEn} size="sm" className="!w-9 !h-9" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-light-100">
              {c.author?.fullNameEn || "Someone"}
            </span>
            <time
              className="text-xs text-accent-ink underline underline-offset-2 decoration-accent-line"
              title={fullTime(c.createdAt)}
            >
              {time(c.createdAt)}
            </time>
            {c.editedAt && !deleted && (
              <span
                className="text-xs text-gray-400"
                title={`Edited ${fullTime(c.editedAt)}`}
              >
                (edited)
              </span>
            )}
          </div>

          {editing ? (
            <div className="mt-1">
              <CommentComposer
                mentionable={mentionable}
                allowFiles={false}
                initialBody={c.body}
                initialMentions={c.mentions}
                submitLabel="Save"
                autoFocus
                onCancel={() => setEditing(false)}
                onSubmit={async ({ body, mentions }) => {
                  await onEditComment(c._id, body, mentions);
                  setEditing(false);
                }}
              />
            </div>
          ) : (
            <div
              className={`mt-1 rounded-xl px-3 py-2 text-sm break-words border ${
                deleted
                  ? "italic text-faint border-dashed border-line2"
                  : `text-ink bg-panel border-line ${isHtml(c.body) ? "" : "whitespace-pre-wrap"}`
              }`}
            >
              {deleted ? (
                "This comment was deleted."
              ) : (
                <CommentBody comment={c} people={people} />
              )}
            </div>
          )}

          {!deleted && c.attachments?.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {c.attachments.map((a) => (
                <AttachmentTile
                  key={a._id}
                  attachment={a}
                  compact
                  canRemove={canRemoveFile(a)}
                  onRemove={onRemoveFile}
                  onPreview={onPreview}
                  onError={onError}
                />
              ))}
            </div>
          )}

          {!deleted && !editing && (mine || access.isAdmin) && (
            <div className="mt-1 flex gap-3 text-xs text-gray-500 dark:text-light-500">
              {mine && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1 hover:text-primary-accent"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1 hover:text-red-600"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          )}
        </div>

        <ConfirmModal
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            onDeleteComment(c);
          }}
          title="Delete comment?"
          message="The comment and its files are removed for everyone. The activity log keeps a record that it was deleted."
          confirmText="Delete"
        />
      </li>
    );
  }

  // System history line.
  return (
    <li className="flex gap-3">
      <Avatar src={item.actor?.photo} name={actorName} size="sm" className="!w-9 !h-9" />
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-[13.5px] leading-snug text-muted break-words">
          <span className="font-semibold text-ink">
            {actorName}
          </span>{" "}
          {describe(item)}
        </p>
        <time
          className="inline-block mt-0.5 text-xs text-accent-ink underline underline-offset-2 decoration-accent-line"
          title={fullTime(item.createdAt)}
        >
          {time(item.createdAt)}
        </time>
        {item.type === "attachment_added" &&
          item.attachment &&
          !item.attachment.deletedAt &&
          !item.meta?.onComment && (
            <div className="mt-1.5">
              <AttachmentTile
                attachment={item.attachment}
                compact
                canRemove={canRemoveFile(item.attachment)}
                onRemove={onRemoveFile}
                onPreview={onPreview}
                onError={onError}
              />
            </div>
          )}
      </div>
    </li>
  );
};

export default ActivityItem;
